import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useLive } from '../lib/useLive';
import { avisar, confirmar, pedirTexto, textoDeError } from './Dialogo';
import { MasMenu } from './MasMenu';
import { Ventana } from './Ventana';

/**
 * CITAS DE HALORY SIN PACIENTE (0.49.0) — arriba de Analítica → Citas, sólo admin.
 *
 * Las asesorías agendan en la Agenda de Halory tecleando nombre, teléfono y correo sin ligar un contacto; el webhook ya
 * busca de quién es (su contacto ligado, o un expediente único por teléfono o correo y, desde 0.49.1, por su nombre exacto)
 * y lo que no pudo resolver queda aquí: nadie con esos datos, varios expedientes con ellos, un nombre igual con otro teléfono
 * o correo, o un contacto de Halory que todavía no está ligado. El reloj las vuelve a revisar solo. El dueño lo quiso
 * "en la parte de citas, no en Alertas, que nadie checa". Cada cita: cuándo (hora de la clínica), su título, quién la
 * agendó, lo que dice de la persona, "¿Es …?" con los candidatos, "Buscar paciente…", "Crear expediente con estos datos" y,
 * en Más, "Descartar…". Pendientes · Asignadas · Descartadas, de 20 en 20. Se refresca sola (tiempo real).
 */
type Estado = 'pendiente' | 'asignada' | 'descartada';
interface Candidato {
  id: string; nombre: string | null; alta: string; sucursal: string | null; etapa: string | null; asesora: string | null;
  telefono: string | null; correo: string | null; citas: number; consultas: number; compras: number; halory: boolean; prueba: boolean;
  /** Por qué es candidato y no se asignó solo ("el nombre coincide pero el teléfono es otro", "se llama igual"). */
  razon?: string | null;
}
/** Lo que se le dijo a su contacto de Halory (cuando la cita traía uno sin ligar y empató por su correo o su nombre). */
interface ContactoH { estado: 'enviado' | 'sin_llave' | 'sin_permiso' | 'error'; motivo: string | null; telefono: 'enviado' | 'no_valido' | 'sin_telefono' | 'no_hace_falta'; refEnOtro?: boolean }
/** Lo que se le puso a la cita de Halory que decía "no dio teléfono". */
interface DatosH { estado: 'enviado' | 'no_valido' | 'sin_llave' | 'sin_permiso' | 'error'; motivo: string | null; clave: string }
interface Liga {
  estado: 'ligada' | 'sin_lead' | 'sin_llave' | 'sin_permiso' | 'error'; motivo: string | null; at: string;
  yaVenia?: boolean; contacto?: ContactoH | null; datos?: DatosH | null;
}
interface Completado { correos: number; telefonos: number }
interface Item {
  id: string; halory_cita_id: string; estado: Estado; estado_halory: string | null; inicio: string | null; titulo: string;
  agendo: string | null; fuente: string | null; servicio: string | null; leadId: string | null;
  persona: { nombre: string | null; telefonos: string[]; correos: string[] };
  candidatos: Candidato[]; motivo: string | null; resolucion: string | null; resuelta_at: string | null; resuelta_por: string | null;
  paciente: { id: string; nombre: string | null; borrado: boolean } | null; appointment_id: string | null; liga_halory: Liga | null;
}
interface Resp {
  conteos: Record<Estado, number>; canceladas: number; estado: Estado; pagina: number; paginas: number; por: number; total: number;
  items: Item[]; disponible: boolean; motivo?: string; halory?: { llave: boolean };
}
interface Encontrado { patient_id: string; nombre: string | null; sucursal: string | null; alta: string; prueba: boolean; telefono: string | null }

const ESTADOS: [Estado, string][] = [['pendiente', 'Pendientes'], ['asignada', 'Asignadas'], ['descartada', 'Descartadas']];
const HALORY_TXT: Record<string, string> = {
  cancelada: 'cancelada en Halory', asistio: 'asistió (según Halory)', no_asistio: 'no llegó (según Halory)', reagendada: 'reagendada', confirmada: 'confirmada',
};
const FUENTE_TXT: Record<string, string> = { panel: 'en la Agenda de Halory', ia: 'la IA de Halory', api: 'por la API de Halory' };
const LIGA_TXT: Record<string, string> = {
  ligada: 'En Halory ya quedó ligada a su contacto', sin_lead: 'En Halory sigue sin contacto: este expediente todavía no tiene contacto allá',
  sin_llave: 'No se pudo ligar en Halory: falta la llave de Halory', sin_permiso: 'No se pudo ligar en Halory: falta el permiso appointments:write en la llave de Halory',
  error: 'No se pudo ligar en Halory',
};
const sinPunto = (t: string | null | undefined) => String(t || '').replace(/[.\s]+$/, '');
/** Lo que contestó Halory al decirle de qué contacto es la cita (y, si hubo, lo que se le dijo a su contacto y a la cita), en
 *  frases con espacio al inicio, para ir después de otra. */
const ligaTxt = (l: Liga | null | undefined) => {
  if (!l) return '';
  const base = l.estado === 'ligada' && l.yaVenia ? 'En Halory la cita ya era de su contacto, y ese contacto quedó ligado a este expediente'
    : LIGA_TXT[l.estado] || LIGA_TXT.error;
  let t = ` ${base}${l.estado === 'error' && l.motivo ? `: ${sinPunto(l.motivo)}` : ''}.`;
  const c = l.contacto; const d = l.datos;
  let dijoInvalido = false;
  if (c) {
    if (c.estado !== 'enviado') t += ` No se pudo actualizar su contacto en Halory: ${sinPunto(c.motivo) || 'Halory contestó con un error'}.`;
    else if (c.telefono === 'enviado') t += ' A su contacto de Halory se le puso el teléfono del expediente.';
    else if (c.telefono === 'no_valido') { t += ' El teléfono del expediente no es un número completo: no se le mandó a Halory.'; dijoInvalido = true; }
  }
  if (d) {
    if (d.estado === 'enviado') t += ' La cita de Halory ya trae su teléfono (decía que no lo dejó).';
    else if (d.estado === 'no_valido') { if (!dijoInvalido) t += ' El teléfono del expediente no es un número completo: no se le mandó a Halory.'; }
    else t += ` No se pudo poner su teléfono en la cita de Halory: ${sinPunto(d.motivo) || 'Halory contestó con un error'}.`;
  }
  return t;
};
/** Lo que se le agregó al expediente con los datos de la cita ("ponemos los 2"). */
const completadoTxt = (c: Completado | null | undefined) => {
  if (!c || (!c.correos && !c.telefonos)) return '';
  const que = [c.correos ? (c.correos === 1 ? 'un correo' : `${c.correos} correos`) : null, c.telefonos ? (c.telefonos === 1 ? 'un teléfono' : `${c.telefonos} teléfonos`) : null].filter(Boolean).join(' y ');
  return ` Al expediente se le agregó ${que} de la cita.`;
};

const CSS = `
.csp { padding: 14px 16px; display: grid; gap: 10px; min-width: 0; }
.csp-cab { display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap; justify-content: space-between; }
.csp-cab h2 { margin: 0; font-size: 16px; font-weight: 600; color: var(--gold-dark); }
.csp-nota { font-size: 12.5px; color: var(--ink-soft); line-height: 1.5; margin: 0; }
.csp-chips { display: flex; gap: 6px; flex-wrap: wrap; }
.csp-chips button { padding: 5px 12px; font-size: 12.5px; box-shadow: none; }
.csp-chips button[aria-pressed="true"] { border-color: var(--gold); background: var(--gold-light); color: var(--gold-dark); font-weight: 700; }
.csp-lista { display: grid; gap: 8px; transition: opacity .15s; min-width: 0; }
.csp-lista.cargando { opacity: .45; pointer-events: none; }
.csp-item { border: 1px solid var(--border); border-radius: 14px; padding: 11px 13px; display: grid; gap: 6px; min-width: 0; background: var(--card, #fff); }
.csp-fila { display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap; min-width: 0; }
.csp-fila b { font-size: 14px; }
.csp-meta { font-size: 12px; color: var(--ink-soft); overflow-wrap: anywhere; min-width: 0; }
.csp-titulo { font-size: 13.5px; line-height: 1.45; overflow-wrap: anywhere; }
.csp-persona { display: flex; gap: 4px 14px; flex-wrap: wrap; font-size: 12.5px; min-width: 0; }
.csp-persona span { overflow-wrap: anywhere; min-width: 0; }
.csp-pill { font-size: 11px; font-weight: 600; padding: 2px 9px; border-radius: 999px; background: var(--bg-alt); color: var(--ink-soft); white-space: nowrap; }
.csp-pill.ambar { background: var(--warning-bg); color: var(--warning); }
.csp-pill.verde { background: var(--success-bg); color: var(--success); }
.csp-cands { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr)); gap: 6px; }
.csp-cand { border: 1px solid var(--border); border-radius: 12px; padding: 8px 10px; display: grid; gap: 3px; background: var(--bg-alt); min-width: 0; font-size: 12.5px; }
.csp-cand a { color: var(--gold-dark); font-weight: 700; overflow-wrap: anywhere; }
.csp-razon { font-size: 12px; color: var(--warning); overflow-wrap: anywhere; }
.csp-acciones { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
.csp-acciones button, .csp-cand button { padding: 5px 12px; font-size: 12.5px; box-shadow: none; }
.csp-pag { display: flex; gap: 10px; align-items: center; justify-content: center; flex-wrap: wrap; font-size: 13px; color: var(--ink-soft); }
.csp-pag button { padding: 6px 14px; font-size: 13px; box-shadow: none; }
.csp-pag button:disabled { opacity: .4; cursor: default; }
.csp-hecho { padding: 8px 12px; border-radius: 10px; font-size: 13px; background: var(--success-bg); color: var(--success); overflow-wrap: anywhere; }
.csp-busca { display: grid; gap: 8px; }
.csp-busca input { width: 100%; }
.csp-res { display: grid; gap: 6px; }
.csp-res > div { display: flex; gap: 8px; align-items: center; justify-content: space-between; border: 1px solid var(--border); border-radius: 12px; padding: 8px 10px; min-width: 0; }
.csp-res > div > span { min-width: 0; overflow-wrap: anywhere; font-size: 13px; }
.csp-res button { flex-shrink: 0; padding: 5px 12px; font-size: 12.5px; box-shadow: none; }
`;

/** Fecha y hora EN LA CLÍNICA (no en la zona de quien mira). */
export const fechaClinica = (iso: string | null) => (iso
  ? new Date(iso).toLocaleString('es-MX', { timeZone: 'America/Mexico_City', weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  : 'sin fecha');
const dia = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City', day: 'numeric', month: 'short', year: 'numeric' }) : '');

export function CitasSinPaciente({ onCambio }: { onCambio?: () => void }) {
  const [estado, setEstado] = useState<Estado>('pendiente');
  const [pagina, setPagina] = useState(1);
  const [d, setD] = useState<Resp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  const [hecho, setHecho] = useState<string | null>(null);
  const [buscarPara, setBuscarPara] = useState<Item | null>(null);
  const ultima = useRef(0);

  const load = (silencioso = false) => {
    const n = ++ultima.current;
    if (!silencioso) setCargando(true);
    return api<Resp>(`crm/citas-por-asignar?estado=${estado}&pagina=${pagina}&por=20`)
      .then((r) => { if (n === ultima.current) { setD(r); setError(null); } })
      .catch((e) => { if (n === ultima.current) setError(textoDeError(e, 'No se pudieron leer las citas de Halory sin paciente.')); })
      .finally(() => { if (n === ultima.current) setCargando(false); });
  };
  useEffect(() => { load(); }, [estado, pagina]);
  useLive(['patient', 'config'], () => load(true));

  const listo = (t: string) => { setHecho(t); load(true); onCambio?.(); };

  const asignar = async (it: Item, pid: string, nombre: string | null) => {
    const trae = it.persona.telefonos.length || it.persona.correos.length;
    if (!(await confirmar({ titulo: '¿Asignar la cita?', confirmar: 'Asignar',
      mensaje: `La cita del ${fechaClinica(it.inicio)} pasa al expediente de ${nombre || 'esta persona'}: sale en su ficha, en el embudo y en estas gráficas.`
        + (trae ? '\nSi la cita trae un teléfono o un correo que ese expediente no tiene, se le agrega (sin borrar los que ya tiene).' : '')
        + (it.leadId ? '\nSu contacto de Halory queda ligado a ese expediente.' : '') }))) return false;
    try {
      const r = await api<{ ligaHalory?: Liga; contactoHalory?: ContactoH; datosHalory?: DatosH; completado?: Completado }>(`crm/citas-por-asignar/${it.id}/asignar`, { method: 'POST', body: JSON.stringify({ patient_id: pid }) });
      const liga = r.ligaHalory ? { ...r.ligaHalory, contacto: r.contactoHalory || null, datos: r.datosHalory || null } : null;
      listo(`Listo: la cita quedó en el expediente de ${nombre || 'esa persona'}.${completadoTxt(r.completado)}${ligaTxt(liga)}`);
      return true;
    } catch (e) { await avisar({ titulo: 'No se asignó', mensaje: textoDeError(e), tipo: 'error' }); load(true); return false; }
  };
  const crear = async (it: Item) => {
    const p = it.persona;
    const datos = [p.nombre || 'sin nombre', p.telefonos[0], p.correos[0]].filter(Boolean).join(' · ');
    if (!(await confirmar({ titulo: '¿Crear un expediente?', confirmar: 'Crear expediente',
      mensaje: `Se crea un expediente nuevo (lead) con ${datos} y la cita queda en él.\nSi ya hay un expediente con ese teléfono, se usa ése en vez de crear otro.` }))) return;
    try {
      const r = await api<{ creado: boolean; nombre: string | null; ligaHalory?: Liga; contactoHalory?: ContactoH; datosHalory?: DatosH }>(`crm/citas-por-asignar/${it.id}/crear-expediente`, { method: 'POST', body: '{}' });
      const liga = r.ligaHalory ? { ...r.ligaHalory, contacto: r.contactoHalory || null, datos: r.datosHalory || null } : null;
      listo(`${r.creado ? `Listo: se creó el expediente de ${r.nombre || 'la persona'}` : `Ya había un expediente con ese teléfono (${r.nombre || 'sin nombre'}): la cita quedó en ése`}.${ligaTxt(liga)}`);
    } catch (e) { await avisar({ titulo: 'No se creó el expediente', mensaje: textoDeError(e), tipo: 'error' }); load(true); }
  };
  const descartar = async (it: Item) => {
    const motivo = await pedirTexto({ titulo: '¿Descartar esta cita?', peligro: true, confirmar: 'Descartar', etiqueta: 'Motivo (opcional)', placeholder: 'p. ej. era una prueba', maxLength: 200,
      mensaje: 'No llega a ningún expediente ni cuenta en las gráficas, y ya no se asigna sola. Se puede volver a pendientes desde «Descartadas».' });
    if (motivo === null) return;
    try {
      await api(`crm/citas-por-asignar/${it.id}/descartar`, { method: 'POST', body: JSON.stringify({ motivo }) });
      listo('Listo: la cita se descartó.');
    } catch (e) { await avisar({ titulo: 'No se descartó', mensaje: textoDeError(e), tipo: 'error' }); load(true); }
  };
  const reabrir = async (it: Item) => {
    try {
      const r = await api<{ estado: string }>(`crm/citas-por-asignar/${it.id}/reabrir`, { method: 'POST', body: '{}' });
      listo(r.estado === 'asignada' ? 'Listo: volvió a pendientes y ahora sí empató con un expediente: quedó asignada.' : 'Listo: volvió a pendientes.');
    } catch (e) { await avisar({ titulo: 'No se pudo', mensaje: textoDeError(e), tipo: 'error' }); load(true); }
  };
  const religar = async (it: Item) => {
    try {
      const r = await api<{ ligaHalory: Liga }>(`crm/citas-por-asignar/${it.id}/ligar-halory`, { method: 'POST', body: '{}' });
      listo(r.ligaHalory.estado === 'ligada' ? 'Listo: en Halory ya quedó ligada a su contacto.' : ligaTxt(r.ligaHalory).trim());
    } catch (e) { await avisar({ titulo: 'No se pudo', mensaje: textoDeError(e), tipo: 'error' }); }
  };

  const c = d?.conteos;
  const desde = d ? (d.pagina - 1) * d.por + 1 : 0;
  const hasta = d ? Math.min(d.total, d.pagina * d.por) : 0;
  return (
    <div className="card csp" data-citas-sin-paciente>
      <style>{CSS}</style>
      <div className="csp-cab">
        <h2>Citas de Halory sin paciente <span data-csp-n>({c ? c.pendiente : '…'})</span></h2>
        {d && d.canceladas > 0 && estado === 'pendiente' && <span className="csp-meta">{d.canceladas} de ellas canceladas en Halory</span>}
      </div>
      <p className="csp-nota">Llegaron de Halory y no se supo de quién son: su teléfono, su correo o su nombre no empatan con un solo expediente, o su contacto de Halory no está ligado. Asígnalas para que salgan en su ficha, en el embudo y aquí; las que después empaten solas (su contacto se liga, o alguien se registra con ese teléfono o ese correo) se asignan sin que nadie haga nada: se vuelven a revisar cada tanto.</p>
      {d && d.disponible === false && <div className="csp-meta" data-csp-sin-tabla>{d.motivo}</div>}
      {error && <div className="csp-meta" style={{ color: 'var(--warning)' }}>{error}</div>}
      {c && d?.disponible !== false && (
        <div className="csp-chips" role="group" aria-label="Estado de las citas">
          {ESTADOS.map(([k, t]) => (
            <button key={k} type="button" className="btn-ghost" aria-pressed={estado === k} data-csp-estado={k}
              onClick={() => { setHecho(null); setPagina(1); setEstado(k); }}>{t} ({c[k] ?? 0})</button>
          ))}
        </div>
      )}
      {hecho && <div role="status" className="csp-hecho" data-csp-hecho>{hecho}</div>}
      {!d && !error && <div className="csp-meta">Cargando…</div>}
      {d && d.disponible !== false && (
        <div className={`csp-lista ${cargando ? 'cargando' : ''}`} aria-busy={cargando}>
          {d.items.length === 0 && (
            <div className="csp-meta" data-csp-vacio>
              {estado === 'pendiente' && 'Todas las citas que llegaron de Halory tienen paciente.'}
              {estado === 'asignada' && 'Todavía no se ha asignado ninguna (ni a mano ni sola).'}
              {estado === 'descartada' && 'No hay citas descartadas.'}
            </div>
          )}
          {d.items.map((it) => (
            <CitaItem key={it.id} it={it} onAsignar={(pid, nombre) => asignar(it, pid, nombre)} onCrear={() => crear(it)} onBuscar={() => setBuscarPara(it)}
              onDescartar={() => descartar(it)} onReabrir={() => reabrir(it)} onReligar={() => religar(it)} />
          ))}
        </div>
      )}
      {d && d.total > d.por && (
        <nav className="csp-pag" aria-label="Páginas de citas sin paciente">
          <button type="button" className="btn-ghost" data-csp-ant disabled={d.pagina <= 1 || cargando} onClick={() => setPagina(d.pagina - 1)}>Anterior</button>
          <span data-csp-pag>{desde}–{hasta} de {d.total}</span>
          <button type="button" className="btn-ghost" data-csp-sig disabled={d.pagina >= d.paginas || cargando} onClick={() => setPagina(d.pagina + 1)}>Siguiente</button>
        </nav>
      )}
      {buscarPara && <BuscarPaciente it={buscarPara} onClose={() => setBuscarPara(null)}
        onElegir={async (p) => { if (await asignar(buscarPara, p.patient_id, p.nombre)) setBuscarPara(null); }} />}
    </div>
  );
}

function CitaItem({ it, onAsignar, onCrear, onBuscar, onDescartar, onReabrir, onReligar }: {
  it: Item; onAsignar: (pid: string, nombre: string | null) => void; onCrear: () => void; onBuscar: () => void;
  onDescartar: () => void; onReabrir: () => void; onReligar: () => void;
}) {
  const p = it.persona;
  const halory = it.estado_halory && HALORY_TXT[it.estado_halory];
  const quien = [it.agendo ? `agendó ${it.agendo}` : 'sin quién la agendó', it.fuente ? FUENTE_TXT[it.fuente] || it.fuente : null].filter(Boolean).join(' · ');
  const religable = it.estado === 'asignada' && it.liga_halory && it.liga_halory.estado !== 'ligada';
  return (
    <div className="csp-item" data-cita-halory={it.id} data-estado={it.estado}>
      <div className="csp-fila">
        <b data-csp-fecha>{fechaClinica(it.inicio)}</b>
        {halory && <span className={`csp-pill ${it.estado_halory === 'cancelada' ? 'ambar' : ''}`} data-csp-halory={it.estado_halory}>{halory}</span>}
        <span className="csp-meta">{quien}</span>
      </div>
      {it.titulo && <div className="csp-titulo" data-csp-titulo>{it.titulo}</div>}
      <div className="csp-persona" data-csp-persona>
        <span><span className="muted">Nombre: </span>{p.nombre || 'no viene'}</span>
        <span><span className="muted">Teléfono: </span>{p.telefonos.length ? p.telefonos.join(' · ') : 'no viene'}</span>
        <span><span className="muted">Correo: </span>{p.correos.length ? p.correos.join(' · ') : 'no viene'}</span>
      </div>

      {it.estado === 'pendiente' && (<>
        {it.motivo && <div className="csp-meta" data-csp-motivo>Por qué no se asignó sola: {it.motivo}.</div>}
        {it.candidatos.length > 0 && (
          <div className="csp-cands" data-csp-candidatos>
            {it.candidatos.map((cnd) => (
              <div key={cnd.id} className="csp-cand" data-candidato={cnd.id}>
                <span>¿Es <Link to={`/paciente/${cnd.id}`}>{cnd.nombre || 'sin nombre'}</Link>?</span>
                {cnd.razon && <span className="csp-razon" data-candidato-razon>{cnd.razon.charAt(0).toUpperCase() + cnd.razon.slice(1)}</span>}
                <span className="csp-meta">{[cnd.telefono, cnd.correo, cnd.alta ? `alta ${dia(cnd.alta)}` : null, cnd.etapa].filter(Boolean).join(' · ')}</span>
                <span className="csp-meta">{[cnd.citas ? `${cnd.citas} cita${cnd.citas === 1 ? '' : 's'}` : 'sin citas', cnd.consultas ? `${cnd.consultas} consulta${cnd.consultas === 1 ? '' : 's'}` : null, cnd.halory ? 'en Halory' : null, cnd.prueba ? 'cuenta de prueba' : null].filter(Boolean).join(' · ')}</span>
                <div><button type="button" className="btn-ghost" data-asignar={cnd.id} onClick={() => onAsignar(cnd.id, cnd.nombre)}>Asignar</button></div>
              </div>
            ))}
          </div>
        )}
        <div className="csp-acciones">
          <button type="button" className="btn-ghost" data-buscar onClick={onBuscar}>Buscar paciente…</button>
          <button type="button" className="btn-ghost" data-crear disabled={!p.telefonos.length}
            title={p.telefonos.length ? 'Un expediente nuevo (lead) con el nombre, teléfono y correo de la cita' : 'La cita no trae teléfono: búscalo o regístralo en Dra. IA'}
            onClick={onCrear}>Crear expediente con estos datos</button>
          <MasMenu datos="cita" items={[{ etiqueta: 'Descartar…', peligro: true, accion: 'descartar', onClick: onDescartar }]} />
        </div>
      </>)}

      {it.estado === 'asignada' && (
        <div className="csp-fila" data-csp-asignada>
          <span className="csp-pill verde">Asignada</span>
          <span className="csp-meta">
            {it.paciente ? <>a <Link to={`/paciente/${it.paciente.id}`} style={{ color: 'var(--gold-dark)', fontWeight: 600 }}>{it.paciente.nombre || 'sin nombre'}</Link>{it.paciente.borrado ? ' (se juntó con otro expediente)' : ''}</> : 'a un expediente que ya no existe'}
            {it.resolucion ? ` · ${it.resolucion}` : ''}{it.resuelta_at ? ` · ${dia(it.resuelta_at)}` : ''}{it.liga_halory ? '.' : ''}{ligaTxt(it.liga_halory)}
          </span>
          {religable && <MasMenu datos="cita" items={[{ etiqueta: 'Volver a ligarla en Halory', accion: 'ligar-halory', onClick: onReligar }]} />}
        </div>
      )}
      {it.estado === 'descartada' && (
        <div className="csp-fila" data-csp-descartada>
          <span className="csp-pill">Descartada</span>
          <span className="csp-meta">{it.resolucion || 'sin motivo'}{it.resuelta_at ? ` · ${dia(it.resuelta_at)}` : ''}</span>
          <MasMenu datos="cita" items={[{ etiqueta: 'Volver a pendientes', accion: 'reabrir', onClick: onReabrir }]} />
        </div>
      )}
    </div>
  );
}

/** Buscar el expediente (nombre o 4+ dígitos del teléfono, el buscador de siempre) y asignarle la cita. */
function BuscarPaciente({ it, onClose, onElegir }: { it: Item; onClose: () => void; onElegir: (p: Encontrado) => void }) {
  const inicial = it.persona.nombre || (it.persona.telefonos[0] || '').replace(/\D/g, '').slice(-10);
  const [q, setQ] = useState(inicial);
  const [res, setRes] = useState<Encontrado[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const campo = useRef<HTMLInputElement>(null);
  useEffect(() => { const t = setTimeout(() => campo.current?.focus(), 30); return () => clearTimeout(t); }, []);
  useEffect(() => {
    const texto = q.trim();
    if (texto.length < 2) { setRes(null); return; }
    let vivo = true;
    const t = setTimeout(() => {
      api<{ resultados: Encontrado[] }>(`pruebas/buscar?q=${encodeURIComponent(texto)}`)
        .then((r) => { if (vivo) { setRes(r.resultados); setErr(null); } })
        .catch((e) => { if (vivo) setErr(textoDeError(e, 'No se pudo buscar.')); });
    }, 350);
    return () => { vivo = false; clearTimeout(t); };
  }, [q]);
  return (
    <Ventana titulo="Buscar paciente" datos="buscar-paciente" ancho={560} onClose={onClose}
      subtitulo={<>La cita del {fechaClinica(it.inicio)}{it.persona.nombre ? ` · ${it.persona.nombre}` : ''}{it.persona.telefonos[0] ? ` · ${it.persona.telefonos[0]}` : ''}</>}>
      <div className="csp-busca">
        <label className="muted" style={{ fontSize: 12.5 }} htmlFor="csp-q">Nombre o 4 o más dígitos del teléfono</label>
        <input id="csp-q" ref={campo} value={q} onChange={(e) => setQ(e.target.value)} placeholder="p. ej. Gabriela o 5510" autoComplete="off" data-buscar-campo />
        {err && <div className="csp-meta" style={{ color: 'var(--warning)' }}>{err}</div>}
        {q.trim().length < 2 && <div className="csp-meta">Escribe al menos 2 letras.</div>}
        {res && res.length === 0 && <div className="csp-meta" data-buscar-vacio>Nadie con eso. Si es alguien nuevo, cierra y usa «Crear expediente con estos datos».</div>}
        {res && res.length > 0 && (
          <div className="csp-res" data-buscar-resultados>
            {res.map((p) => (
              <div key={p.patient_id} data-encontrado={p.patient_id}>
                <span><b>{p.nombre || 'Sin nombre'}</b><span className="csp-meta"> · {[p.telefono, p.sucursal, p.alta ? `alta ${dia(p.alta)}` : null, p.prueba ? 'cuenta de prueba' : null].filter(Boolean).join(' · ')}</span></span>
                <button type="button" className="btn-ghost" data-elegir={p.patient_id} onClick={() => onElegir(p)}>Asignar</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Ventana>
  );
}
